<!-- The run window every user gets (XAT-E-377-05). The floating Xact Playground shows only the runs it started, while it
     is up (XCT-E-12-05): one run, one window. -->
@if (run && !hostedElsewhere) {
  <!-- ─── Folded: a tab DOCKED to the bottom edge (review, 2026-09-27), as the floating Xact Playground's is — always in
       the same place, clear of the page, never dragged. What a run raises by default. ─── -->
  @if (collapsed) {
    <!-- WHAT THE TAB SAYS, AND WHAT IT CAN DO WITHOUT OPENING (review, 2026-09-27): the run's state in words — Running,
         then its outcome — and what a reader wants from here without opening it: stop the run, or put it away. The
         tab's body opens the window on the run's Execution log. -->
    <div class="action-monitor-dock"
         id="action-monitor-pill"
         data-cy="btn-monitor-dock"
         role="group"
         aria-label="Action Monitor"
         [title]="'Action Monitor — ' + statusLabel">
      <button type="button" class="am-dock-main" data-cy="btn-monitor-dock-open" (click)="openOn('log')"
              aria-label="Open the Action Monitor">
        <!-- COLLAPSED, THE DOT IS NAMED (XAT-E-265-03): hue alone was the state before; the tooltip says the same word. -->
        <span class="am-dot"
              role="img"
              [attr.aria-label]="statusLabel"
              [class.running]="isRunning"
              [class.ok]="run.status === 'completed'"
              [class.bad]="run.status === 'failed'"
              [class.halted]="run.status === 'stopped' || run.status === 'timed-out'"></span>
        <span class="am-dock-text">
          <span class="am-dock-label">Action Monitor</span>
          <span class="am-dock-status">{{ dockStatus }}</span>
        </span>
        <span class="am-dock-count">{{ counter }}</span>
      </button>
      <span class="am-dock-actions">
        <!-- MISN-E-224-06: a mission tab that left its mission for a screen run goes back in one click. -->
        @if (awayFromMission) {
          <button type="button" class="am-dock-btn" data-cy="btn-monitor-dock-back-to-mission" (click)="backToMission()"
                  title="Back to the mission">Back to the mission</button>
        }
        @if (canStopFromDock) {
          <button type="button" class="am-dock-btn am-dock-btn--stop" data-cy="btn-monitor-dock-stop" (click)="stopRun()"
                  title="Stop this run">Stop</button>
        }
        <button type="button" class="am-dock-icon" data-cy="btn-monitor-dock-expand" (click)="openOn('log')"
                title="Open" aria-label="Open the Action Monitor">
          <i class="fa-solid fa-chevron-up" aria-hidden="true"></i>
        </button>
        <button type="button" class="am-dock-icon" data-cy="btn-monitor-dock-close" (click)="close()"
                title="Close" aria-label="Close the Action Monitor">
          <i class="fa-regular fa-xmark" aria-hidden="true"></i>
        </button>
      </span>
    </div>
  }

  <!-- ─── The panel: its frame and chrome here, its body the run view (XCT-E-12-05) ─── -->
  @if (!collapsed) {
    <div class="action-monitor"
         id="action-monitor"
         [class.action-monitor--chat]="showsRunChat"
         [class.action-monitor--large]="showsRunChat && isLarge"
         cdkDrag
         [cdkDragConstrainPosition]="keepDragInView"
         [cdkDragFreeDragPosition]="dragPosition"
         (cdkDragEnded)="onDragEnded($event)">
      @if (showsRunChat) {
        <!-- A CHAT'S RUN (XAT-E-377): two tabs under one bar that carries the run's state — the run's log, and the
             conversation it came from. The chat stays mounted on the Execution tab, so switching never reloads it. -->
        <div class="am-tabbar" cdkDragHandle>
          <!-- THE XATBOT WINDOW'S HEADER, AS IT IS DRAWN (review, 2026-09-27; platform-floating-xatbot): its own icon at
               the left, the segmented toggle in the middle, the window controls at the right. -->
          <div class="am-tabbar-side">
            <!-- Continue in the XatBot panel — the panel's own "Dock to sidebar" glyph, which is what this does. -->
            <button type="button" class="am-tbtn" (click)="continueInXatBot()" data-cy="btn-monitor-open-in-xatbot"
                    title="Continue this conversation in the XatBot panel" aria-label="Open in XatBot">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                <rect x="-0.75" y="0.75" width="14.5" height="14.5" rx="2.25" transform="matrix(-1 0 0 1 14.5 0)" stroke="currentColor" stroke-width="1.5"/>
                <path d="M4.5 6.75H0.75V13C0.75 14.2426 1.75736 15.25 3 15.25H6.25V8.5C6.25 7.5335 5.4665 6.75 4.5 6.75Z" stroke="currentColor" stroke-width="1.5"/>
                <path d="M11.5 7.79688L11.4964 4.79687H8.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
              </svg>
            </button>
          </div>
          <div class="am-tabs" role="tablist" aria-label="Run">
            <button type="button" role="tab" class="am-tab" data-cy="tab-monitor-run-log"
                    [class.am-tab--active]="runTab === 'log'" [attr.aria-selected]="runTab === 'log'"
                    (click)="showRunTab('log')">
              Execution
            </button>
            <button type="button" role="tab" class="am-tab" data-cy="tab-monitor-chat"
                    [class.am-tab--active]="runTab === 'chat'" [attr.aria-selected]="runTab === 'chat'"
                    (click)="showRunTab('chat')">
              Chat
            </button>
          </div>
          <div class="am-tabbar-side am-tabbar-side--end">
            <button type="button" class="am-tbtn" (click)="toggleSize()" data-cy="btn-monitor-expand"
                    [title]="isLarge ? 'Collapse to small view' : 'Expand to large view'"
                    [attr.aria-label]="isLarge ? 'Collapse to small view' : 'Expand to large view'">
              @if (isLarge) {
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
                  <path d="M0 8H6V10H0V8Z" fill="currentColor" />
                  <path d="M4 8H6V14H4V8Z" fill="currentColor" />
                  <path d="M14 6H8V4H14V6Z" fill="currentColor" />
                  <path d="M10 6H8V0H10V6Z" fill="currentColor" />
                </svg>
              } @else {
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
                  <path d="M8 0H14V2H8V0Z" fill="currentColor" />
                  <path d="M12 0H14V6H12V0Z" fill="currentColor" />
                  <path d="M6 14H0V12H6V14Z" fill="currentColor" />
                  <path d="M2 14H0V8H2V14Z" fill="currentColor" />
                </svg>
              }
            </button>
            <button type="button" class="am-tbtn" (click)="minimize()" title="Minimize" aria-label="Minimize">
              <i class="fa-solid fa-minus" aria-hidden="true"></i>
            </button>
            <button type="button" class="am-tbtn am-tbtn--close" (click)="close()" title="Close" aria-label="Close the Action Monitor">
              <i class="fa-regular fa-xmark" aria-hidden="true"></i>
            </button>
          </div>
        </div>
        <div class="am-tab-pane" role="tabpanel" [class.am-tab-pane--hidden]="runTab !== 'chat'">
          <app-run-thread-chat [threadId]="run.chatThreadId" [agentType]="runChatAgent"></app-run-thread-chat>
        </div>
        @if (runTab === 'log') {
          <!-- THE EXECUTION TAB, FOR A READER (review, 2026-09-27): what the run is doing in words, with its progress,
               over the steps as a timeline — the chat's type and spacing, not the developer's log strip. -->
          <div class="am-exec" role="tabpanel">
            <div class="am-exec-summary" [ngClass]="run.status" data-cy="monitor-exec-summary">
              <span class="am-exec-icon" aria-hidden="true">
                @switch (run.status) {
                  @case ('running') { <i class="fa-solid fa-spinner fa-spin"></i> }
                  @case ('completed') { <i class="fa-solid fa-check"></i> }
                  @case ('failed') { <i class="fa-solid fa-xmark"></i> }
                  @default { <i class="fa-solid fa-stop"></i> }
                }
              </span>
              <span class="am-exec-text">
                <span class="am-exec-title">{{ execTitle }}</span>
                <span class="am-exec-sub">{{ execSubtitle }}</span>
              </span>
              <span class="am-exec-count">{{ counter }}</span>
              <span class="am-exec-bar" aria-hidden="true">
                <span class="am-exec-bar-fill" [style.width.%]="execPercent"></span>
              </span>
            </div>
            <app-action-monitor-run-view class="am-run-log" [titleless]="true" [runActionless]="true" [reader]="isChatRun"></app-action-monitor-run-view>
          </div>
        }
      } @else {
        <!-- A CHAT'S RUN IS SHOWN TO ITS READER HERE TOO (XAT-E-526-03): plain numbered steps, no Re-run or Copy log —
             a run whose thread this window cannot host is still a run someone asked for in a chat. -->
        <app-action-monitor-run-view [reader]="isChatRun" [runActionless]="isChatRun">
          <!-- A run thread the window cannot host (XAT-E-377-03): its agent reads its page. The thread is a click away. -->
          @if (awayFromMission) {
            <button runViewChrome type="button" class="am-tbtn" (click)="backToMission()" data-cy="btn-monitor-back-to-mission"
                    title="Back to the mission" aria-label="Back to the mission">
              <i class="fa-solid fa-arrow-left" aria-hidden="true"></i>
            </button>
          }
          @if (hasRunThread) {
            <button runViewChrome type="button" class="am-tbtn" (click)="openRunChat()" data-cy="btn-monitor-open-chat"
                    title="This conversation belongs to its page's agent. Open it in the XatBot panel" aria-label="Open chat">
              <i class="fa-solid fa-comment" aria-hidden="true"></i>
            </button>
          }
          <!-- ICON-ONLY, SO THE NAME IS THE BUTTON'S AND THE GLYPH IS HIDDEN (XAT-E-265-03). A `title` is a
               tooltip, not a label: it names the control only for software that falls back to it, and these
               are this panel's whole window chrome. -->
          <button runViewChrome type="button" class="am-tbtn" (click)="minimize()" title="Minimise" aria-label="Minimise">
            <i class="fa-solid fa-minus" aria-hidden="true"></i>
          </button>
          <button runViewChrome type="button" class="am-tbtn" (click)="close()" title="Close"
                  aria-label="Close the Action Monitor">
            <i class="fa-solid fa-xmark" aria-hidden="true"></i>
          </button>
        </app-action-monitor-run-view>
      }
    </div>
  }
}
